/**
 * Authenticated fetch wrapper.
 *
 * Pulls a session token from App Bridge on every call (tokens are short-lived,
 * so we don't cache) and sends it as a Bearer header. The Cloudflare Pages
 * Functions middleware verifies the JWT before any handler runs.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function getSessionToken(): Promise<string> {
  // App Bridge is loaded via script tag in index.html. If shopify global is
  // missing, we're either outside the embedded admin context or the script
  // failed to load.
  if (typeof window === 'undefined' || !window.shopify) {
    throw new ApiError(
      0,
      null,
      'App Bridge not initialised. This app must be loaded inside Shopify admin.',
    )
  }
  return await window.shopify.idToken()
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  signal?: AbortSignal
}

export async function api<TResponse = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<TResponse> {
  const token = await getSessionToken()

  const response = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  let parsedBody: unknown = null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    parsedBody = await response.json().catch(() => null)
  } else {
    parsedBody = await response.text().catch(() => null)
  }

  if (!response.ok) {
    const message =
      parsedBody && typeof parsedBody === 'object' && 'error' in parsedBody
        ? String((parsedBody as { error: unknown }).error)
        : `Request to ${path} failed with status ${response.status}`
    throw new ApiError(response.status, parsedBody, message)
  }

  return parsedBody as TResponse
}
